import { AnswerType, AnswerValueType, getSequenceAnswersAnswerType } from "../types/Answer";
import { Dependency, NumberRule } from "../types/Dependency";
import { QuestionType, QuestionTypeEnum, RuntimeInstance } from "../types/Question";
import { QuestionnaireDetailsType } from "../types/Questionnaire";
import { hasAsweredFunctionsByType, getAnswerValue } from "../helper/answer"; // adjust import

/* ----------------------------- Set engine ----------------------------- */
// Follow-up sets: a checkbox option or a number question can reveal a "set" of
// questions. Members live in `questions` (set_driver_question_id != null) but are
// pulled out of the linear flow and injected as instances when their driver is
// answered. See [[followup-sets]] for the data model.

// Locale-aware ordinal suffixes for the study's supported languages, selected via the
// runtime's Intl.PluralRules (ordinal). Admins never write these and they're never
// translated — the ordinal is a pure function of (number, language). Any language not
// listed falls back to the plain number, so it degrades gracefully and never breaks.
const ORDINAL_SUFFIX: Record<string, Record<string, string>> = {
  en: { one: "st", two: "nd", few: "rd", other: "th" },
  fr: { one: "er", other: "e" },
  // Add more study languages here as needed (e.g. es: { other: "º" }).
};

export const formatOrdinal = (n: number, lang: string): string => {
  const base = (lang || "").split("-")[0].toLowerCase();
  const suffixes = ORDINAL_SUFFIX[base];
  if (!suffixes) return String(n);
  try {
    const category = new Intl.PluralRules(lang, { type: "ordinal" }).select(n);
    return `${n}${suffixes[category] ?? suffixes.other ?? ""}`;
  } catch {
    return String(n);
  }
};

export const formatRepetitionLabel = (
  template: string | null | undefined,
  n: number,
  lang = "en",
): string =>
  (template || "")
    .replace(/\{ordinal\}/gi, formatOrdinal(n, lang))
    .replace(/\{n\}/gi, String(n));

const bySetOrder = (a: QuestionType, b: QuestionType): number => (a.set_order ?? 0) - (b.set_order ?? 0);

export const isSetMember = (q: QuestionType): boolean => q.set_driver_question_id != null;

export const topLevelQuestions = (questions: QuestionType[]): QuestionType[] =>
  questions.filter((q) => !isSetMember(q)).sort((a, b) => a.order - b.order);

const membersOf = (driverId: number, questions: QuestionType[]): QuestionType[] =>
  questions.filter((q) => q.set_driver_question_id === driverId);

const isDriver = (q: QuestionType, questions: QuestionType[]): boolean =>
  membersOf(q.id, questions).length > 0;

const cloneInstance = (q: QuestionType, runtime: RuntimeInstance): QuestionType =>
  ({ ...q, runtime } as QuestionType);

/**
 * Stable key identifying one set-member instance, and the single source of truth for
 * that format (used both when expanding the flow and when restoring saved answers).
 * Number repeats key by iteration index (`i`); checkbox fan-out keys by the option id
 * (`o`) — which is also the answer's instance_index, so each option's answers persist
 * and restore distinctly.
 */
const setInstanceKey = (driver: QuestionType, instanceIndex: number, memberId: number): string =>
  driver.type === QuestionTypeEnum.CHECKBOX
    ? `d${driver.id}:o${instanceIndex}:q${memberId}`
    : `d${driver.id}:i${instanceIndex}:q${memberId}`;

const findAnswer = (
  answers: AnswerType[],
  questionId: number,
  instanceKey: string | null
): AnswerType | undefined =>
  answers.find((a) => a.questionId === questionId && (a.instanceKey ?? null) === instanceKey);

/** Expand a driver question into the ordered instances its current answer reveals. */
export const expandDriver = (
  driver: QuestionType,
  answerValue: AnswerValueType | undefined,
  questions: QuestionType[],
  lang = "en"
): QuestionType[] => {
  const members = membersOf(driver.id, questions);
  if (members.length === 0) return [];

  if (driver.type === QuestionTypeEnum.NUMBER) {
    const repeatMembers = members.filter((m) => m.set_trigger_answer_id == null).sort(bySetOrder);
    const raw = Number(answerValue);
    const n = Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;
    const label = (driver.detail as { repetition_label?: string | null })?.repetition_label;
    const out: QuestionType[] = [];
    // With a single repetition there's nothing to disambiguate, so skip the
    // per-iteration heading ("First MRI" etc.) — show it only when n > 1.
    for (let i = 1; i <= n; i++) {
      repeatMembers.forEach((m, j) => {
        out.push(cloneInstance(m, {
          key: setInstanceKey(driver, i, m.id),
          driverId: driver.id,
          instanceIndex: i,
          triggerAnswerId: null,
          heading: j === 0 && n > 1 ? formatRepetitionLabel(label, i, lang) : null,
        }));
      });
    }
    return out;
  }

  if (driver.type === QuestionTypeEnum.CHECKBOX) {
    const selected = Array.isArray(answerValue)
      ? answerValue
      : answerValue != null ? [answerValue] : [];
    const options = (driver.detail as { answers?: Array<{ id: number; label: string }> })?.answers ?? [];
    const out: QuestionType[] = [];
    options.forEach((opt) => {
      if (!selected.includes(opt.id)) return;
      const optMembers = members.filter((m) => m.set_trigger_answer_id === opt.id).sort(bySetOrder);
      optMembers.forEach((m, j) => {
        out.push(cloneInstance(m, {
          // instanceIndex = option id (not a constant 1) so each selected option's
          // member answers persist under a distinct instance_index instead of colliding.
          key: setInstanceKey(driver, opt.id, m.id),
          driverId: driver.id,
          instanceIndex: opt.id,
          triggerAnswerId: opt.id,
          heading: j === 0 ? opt.label : null,
        }));
      });
    });
    return out;
  }

  return [];
};

/**
 * Expected number of steps in the (instance-expanded) flow, for the progress bar.
 * Set members are pulled out of the linear flow, so the base is the top-level
 * questions; each answered driver then contributes the instances its current answer
 * reveals (N×members for number, one set per selected option for checkbox). The total
 * grows as drivers are answered — matching what the user will actually be shown — so
 * `history.length / total` no longer exceeds 100%.
 */
export const countFlowQuestions = (
  questions: QuestionType[],
  answers: AnswerType[]
): number => {
  const top = topLevelQuestions(questions);
  let total = top.length;
  for (const q of top) {
    if (isDriver(q, questions)) {
      const driverAnswer = findAnswer(answers, q.id, null);
      total += expandDriver(q, driverAnswer?.value, questions).length;
    }
  }
  return total;
};

/**
 * Rebuild the answers for set-member instances after a reload. Saved set answers carry an
 * instance_index (>0); each is mapped back to its runtime instance via setInstanceKey, so
 * the value lands on the right instance instead of showing empty. Top-level answers
 * (instance_index 0) are handled by the normal per-question seeding and skipped here.
 */
export const restoreInstanceAnswers = (
  questions: QuestionType[],
  formattedAnswers: getSequenceAnswersAnswerType[]
): AnswerType[] => {
  const out: AnswerType[] = [];
  for (const ans of formattedAnswers) {
    const idx = Number(ans.instance_index) || 0;
    if (idx <= 0) continue;
    const member = questions.find((q) => q.id === ans.question_id);
    if (!member || member.set_driver_question_id == null) continue;
    const driver = questions.find((q) => q.id === member.set_driver_question_id);
    if (!driver) continue;
    out.push({
      questionId: member.id,
      questionType: member.type,
      value: getAnswerValue(member.type, ans),
      instanceKey: setInstanceKey(driver, idx, member.id),
    });
  }
  return out;
};

/** The selected choice ids of a select-type answer, normalized to numbers (checkbox → array). */
const selectedAnswerIds = (answer: AnswerType | undefined): number[] => {
  const value = answer?.value;
  const raw = Array.isArray(value) ? value : value != null ? [value] : [];
  return raw
    .map((v) => (typeof v === "string" ? Number(v) : v))
    .filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
};

/**
 * Does the answer select a choice flagged "End Questionnaire"? Works for both single-choice
 * (radio/dropdown) and multi-choice (checkbox): if ANY selected option carries endAfter, the
 * questionnaire ends. Depends on `dependency.answers[].endAfter` being populated (the API deps
 * mapper now returns it for CHECKBOX as well as RADIOBUTTON/DROPDOWN).
 */
const selectsEndAfterChoice = (
  dependency: Dependency | undefined,
  answer: AnswerType | undefined
): boolean => {
  if (!dependency?.answers?.length) return false;
  const selected = selectedAnswerIds(answer);
  return dependency.answers.some((a) => a.endAfter && selected.includes(a.id));
};

/**
 * A NUMBER session answer as a real number, or null when there is nothing usable.
 * Deliberately NOT Number(): during a live run the value is a STRING typed keystroke
 * by keystroke (see AnswerNumber), and Number("") === 0 would make an unanswered
 * question satisfy a "<= 0" rule and silently reroute the flow. Mirrors the parseFloat
 * that isNumberAnswerValid already uses.
 */
const numericAnswerValue = (value: AnswerValueType | undefined): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null; // number[] = checkbox, never ours
  const normalized = value.trim().replace(",", ".");
  // Intermediate states while typing a negative/decimal ("-", ".", "-.") are not answers.
  if (normalized === "" || normalized === "-" || normalized === "." || normalized === "-.")
    return null;
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The threshold rule that decides a NUMBER question's exit, or undefined when the
 * question has no rules / nothing usable was answered / nothing matched.
 * Shared by resolveExit (routing) and hasEndAfterBeenMet (completion) — the two MUST
 * agree, or a matched rule routes forward while the completion heuristic ends the run.
 */
export const matchedNumberRule = (
  dependency: Dependency | undefined,
  questionType: string | undefined,
  value: AnswerValueType | undefined
): NumberRule | undefined => {
  if (questionType !== QuestionTypeEnum.NUMBER) return undefined;
  if (!dependency?.number_rules?.length) return undefined;
  const numeric = numericAnswerValue(value);
  if (numeric === null) return undefined;
  return [...dependency.number_rules]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .find((rule) => matchesNumberRule(rule, numeric));
};

/** Does a threshold rule match the given numeric answer? BETWEEN is inclusive. */
const matchesNumberRule = (rule: NumberRule, value: number): boolean => {
  switch (rule.operator) {
    case "GT": return value > rule.value;
    case "GTE": return value >= rule.value;
    case "LT": return value < rule.value;
    case "LTE": return value <= rule.value;
    case "EQ": return value === rule.value;
    case "NEQ": return value !== rule.value;
    case "BETWEEN":
      return rule.value_max != null && value >= rule.value && value <= rule.value_max;
    default: return false; // unknown operator from a newer API: never match
  }
};

/** The next top-level question by order (set members are skipped), or null at the end. */
const nextTopLevelAfter = (
  question: QuestionType,
  questions: QuestionType[]
): QuestionType | null => {
  const top = topLevelQuestions(questions);
  const idx = top.findIndex((q) => q.id === question.id);
  return idx >= 0 ? top[idx + 1] ?? null : null;
};

/** Where the flow goes after a question/driver: explicit routing, else next top-level. */
const resolveExit = (
  question: QuestionType,
  questions: QuestionType[],
  dependencies: Dependency[],
  answers: AnswerType[]
): QuestionType | null => {
  const dependency = dependencies.find((d) => d.id === question.id);
  const answer = findAnswer(answers, question.id, null);

  let questionDepId: number | undefined;
  if (dependency?.answers && dependency.answers.length > 0) {
    // An "End Questionnaire" choice ends the flow (single- or multi-choice).
    if (selectsEndAfterChoice(dependency, answer)) return null;
    if (question.type === QuestionTypeEnum.CHECKBOX) {
      // Multi-choice options have no single branching target (they reveal follow-up sets or
      // end). A question-level "Continue to → End Questionnaire" (set after the sets) still
      // applies; otherwise fall through to its exit / next-in-order.
      if (dependency.endAfter) return null;
    } else if (selectedAnswerIds(answer).length === 0) {
      // "No choice selected" (non-mandatory question skipped): its own routing case.
      // selectedAnswerIds also rejects the NaN a radio deselect leaves behind.
      if (dependency.no_answer_end_after) return null;
      questionDepId = dependency.no_answer_next_question?.id ?? undefined;
    } else {
      // Single-select branching: route to the chosen answer's target (unchanged behavior).
      const answerDep = dependency.answers.find((a) => a.id === answer?.value);
      questionDepId = answerDep?.next_question?.id;
    }
  } else if (
    // NUMBER threshold routing: ordered rules on the VALUE of the answer, first match
    // wins. Distinct from the repeating follow-up set, which reads the same answer as a
    // COUNT — and reached only AFTER a driver's set instances are exhausted, because
    // getNextQuestion enters the expansion before ever calling resolveExit.
    question.type === QuestionTypeEnum.NUMBER &&
    dependency?.number_rules &&
    dependency.number_rules.length > 0
  ) {
    const matched = matchedNumberRule(dependency, question.type, answer?.value);
    if (matched) {
      if (matched.endAfter) return null;
      // A matched rule with no target means "the next question in order" — it must NOT
      // fall through to the question-level exit below, which is the OTHERWISE target.
      if (matched.next_question?.id == null) return nextTopLevelAfter(question, questions);
      questionDepId = matched.next_question.id;
    } else if (dependency.endAfter) {
      // No rule matched → the question-level exit is the "otherwise" branch.
      return null;
    }
  } else if (dependency?.endAfter) {
    return null;
  }

  if (!questionDepId) questionDepId = dependency?.next_question?.id ?? undefined;
  if (questionDepId) return questions.find((q) => q.id === questionDepId) ?? null;

  return nextTopLevelAfter(question, questions);
};

/**
 * The next question to show. `current` is the runtime question (it may be a set
 * instance, carrying `current.runtime`). With no sets this reduces to the
 * original linear/skip-logic behavior.
 */
export function getNextQuestion(
  questions: QuestionType[],
  dependencies: Dependency[],
  current: QuestionType,
  answers: AnswerType[],
  lang = "en"
): QuestionType | null {
  // 1. Inside a set: advance to the next member instance, else exit via the driver.
  if (current.runtime) {
    const driver = questions.find((q) => q.id === current.runtime!.driverId);
    if (!driver) return null;
    const driverAnswer = findAnswer(answers, driver.id, null);
    const expansion = expandDriver(driver, driverAnswer?.value, questions, lang);
    const pos = expansion.findIndex((e) => e.runtime!.key === current.runtime!.key);
    if (pos >= 0 && pos < expansion.length - 1) return expansion[pos + 1];
    return resolveExit(driver, questions, dependencies, answers);
  }

  // 2. A driver question: enter its first revealed instance, if any — unless an "End
  //    Questionnaire" choice was selected, which ends the flow before any follow-up set runs.
  if (isDriver(current, questions)) {
    const dependency = dependencies.find((d) => d.id === current.id);
    const driverAnswer = findAnswer(answers, current.id, null);
    if (selectsEndAfterChoice(dependency, driverAnswer)) return null;
    const expansion = expandDriver(current, driverAnswer?.value, questions, lang);
    if (expansion.length > 0) return expansion[0];
  }

  // 3. Normal routing (set members skipped in the fallthrough).
  return resolveExit(current, questions, dependencies, answers);
}

const hasLastQuestionBeenAnswered = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  if (!dependencies?.length) return false;

  // last by order
  const lastDep = dependencies.reduce<Dependency | null>((acc, d) => {
    if (!acc) return d;
    return (d.order ?? 0) > (acc.order ?? 0) ? d : acc;
  }, null);

  if (!lastDep) return false;

  // ✅ compare by questionId (dependency.id is the question id)
  return sessionAnswers.some(
    (a) =>
      a.questionId === lastDep.id &&
      (hasAsweredFunctionsByType[a.questionType]?.(a.value) ?? false)
  );
};


const hasEndAfterBeenMet = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  for (const ans of sessionAnswers) {
    const dep = dependencies.find((d) => d.id === ans.questionId);
    if (!dep) continue;

    const isAnswered = hasAsweredFunctionsByType[ans.questionType]?.(ans.value) ?? false;
    if (!isAnswered) continue;

    // A NUMBER with threshold rules redefines dep.endAfter as the OTHERWISE branch: it
    // ends the questionnaire only when NO rule matched, and a matched rule ends it only
    // if that rule says so. Without this carve-out, "Otherwise → End Questionnaire"
    // would end the run the moment the number is answered — making every rule dead.
    // Must stay in lockstep with resolveExit (both go through matchedNumberRule).
    if (dep.number_rules?.length && ans.questionType === QuestionTypeEnum.NUMBER) {
      const matched = matchedNumberRule(dep, ans.questionType, ans.value);
      if (matched) {
        if (matched.endAfter) return true;
        continue; // routed somewhere: this question does not end the questionnaire
      }
      if (dep.endAfter) return true;
      continue;
    }

    if (dep.endAfter) return true;

    if (dep.answers?.length) {
      const selectedIds = (Array.isArray(ans.value) ? ans.value : [ans.value])
        .map((v) => (typeof v === 'string' ? Number(v) : v))
        .filter((v) => v != null && !Number.isNaN(v as number)) as number[];

      if (dep.answers.some((a) => selectedIds.includes(a.id) && a.endAfter)) {
        return true;
      }
    }
  }
  return false;
};



export const isQuestionnaireDone = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  if (!dependencies?.length) return false;
  return (
    hasEndAfterBeenMet(dependencies, sessionAnswers) ||
    hasLastQuestionBeenAnswered(dependencies, sessionAnswers)
  );
};

/**
 * Calculate total score of a questionnaire if it is scored
 * @param questions - Questionnaire questions with answers + scores
 * @param sessionAnswers - User's answers
 * @param is_scored - Whether the questionnaire is scored
 */
export function calculateQuestionnaireScore(
  questions: QuestionType[],
  sessionAnswers: AnswerType[],
  is_scored: boolean
): number {
  if (!is_scored) return null;

  let total = 0;

  for (const ans of sessionAnswers) {
    const question = questions.find((q) => q.id === ans.questionId);
    if (!question) continue;

    const coefficient = question.score_coefficient ?? 1;

    // ▼ SELECT-TYPE QUESTIONS ---------------------------------------
    if (["RADIOBUTTON", "CHECKBOX", "DROPDOWN"].includes(question.type)) {
      const detail: any = question.detail;
      if (!detail?.answers) continue;

      // Checkbox returns array, others return single value
      const selectedIds = Array.isArray(ans.value) ? ans.value : [ans.value];

      for (const id of selectedIds) {
        const answerDef = detail.answers.find((a: any) => a.id === id);
        if (answerDef && answerDef.score != null) {
          total += Number(answerDef.score) * coefficient;
        }
      }
    }

    // ▼ SLIDER QUESTIONS --------------------------------------------
    if (question.type === "SLIDER") {
      const value = Number(ans.value) || 0;
      total += value * coefficient;
    }

    if (question.type === "NUMBER") {
      const value = Number(ans.value) || 0;
      total += value * coefficient;
    }
  }

  return total;
}